/**
 * Semester and School Days Query Functions
 * 
 * Fetches semester data and calculates school days.
 * @module fehlzeiten/services/queries/semesterQueries
 */

import { supabase } from '../../../../../lib/supabase';

export interface SemesterData {
    id: string;
    name?: string;
    start_date: string;
    end_date: string;
    is_active: boolean;
}

export interface CalendarException {
    date: string;
    end_date: string | null;
    type: string;
}

/**
 * Fetches all semesters for a school.
 */
export async function fetchSemesters(schoolId: string): Promise<SemesterData[]> {
    const { data, error } = await supabase
        .from('structure_school_semesters' as never)
        .select('id, name, start_date, end_date, is_active' as never)
        .eq('school_id' as never, schoolId as never)
        .order('start_date' as never, { ascending: true });

    if (error) {
        console.error('[fetchSemesters] Error:', error);
        return [];
    }

    return (data as unknown as SemesterData[]) || [];
}

/**
 * Fetches calendar exceptions for a date range.
 */
export async function fetchCalendarExceptions(
    schoolId: string,
    startDate: string,
    endDate: string
): Promise<CalendarException[]> {
    const { data, error } = await supabase
        .from('schedule_calendar_exceptions' as never)
        .select('date, end_date, type' as never)
        .eq('school_id' as never, schoolId as never)
        .lte('date' as never, endDate as never)
        .gte('end_date' as never, startDate as never);

    if (error) {
        console.error('[fetchCalendarExceptions] Error:', error);
        return [];
    }

    return (data as unknown as CalendarException[]) || [];
}

/**
 * Calculates the number of school days using RPC.
 */
export async function calculateSchoolDaysRpc(
    schoolId: string,
    startDate: string,
    endDate: string
): Promise<number> {
    const { data, error } = await supabase.rpc('fn_calculate_school_days', {
        p_school_id: schoolId,
        p_start_date: startDate,
        p_end_date: endDate,
    });

    if (error) {
        console.error('[calculateSchoolDaysRpc] RPC error:', error);
        return 0;
    }

    return (data as number) || 0;
}

/**
 * Calculates the number of school days (client-side, deprecated).
 * @deprecated Use calculateSchoolDaysRpc instead
 */
export function calculateSchoolDays(
    startDate: string,
    endDate: string,
    exceptions: CalendarException[]
): number {
    const exceptSet = new Set<string>();
    for (const exc of exceptions) {
        const from = new Date(exc.date);
        const to = new Date(exc.end_date || exc.date);
        for (let d = new Date(from); d <= to; d.setDate(d.getDate() + 1)) {
            exceptSet.add(d.toISOString().slice(0, 10));
        }
    }

    const from = new Date(startDate);
    const to = new Date(endDate);
    let count = 0;
    for (let d = new Date(from); d <= to; d.setDate(d.getDate() + 1)) {
        const day = d.getDay();
        const iso = d.toISOString().slice(0, 10);
        if (day === 0 || day === 6) continue;
        if (exceptSet.has(iso)) continue;
        count++;
    }

    return count;
}
